import React, { useEffect, useMemo, useState } from 'react'
import { adminRequest } from '../api/client'
import AdminFormModal, { FormField, FormGrid } from '../components/AdminFormModal'
import { Card, EmptyState, PageTitle, SearchField, StatusBadge, formatDate } from '../components/Ui'
import '../ai-guidelines.css'

const scopeLabels = {
  GLOBAL: '전체 AI',
  QUOTE_ANALYSIS: '견적 분석',
  CURATION: '디렉터 큐레이션',
  SEARCH: '검색',
}
const statusLabels = { DRAFT: '작성중', ACTIVE: '적용중', ARCHIVED: '보관' }

function emptyEditor(item = null) {
  return item ? { ...item } : { id: null, version: null, title: '', body: '', scope: 'GLOBAL', status: 'DRAFT', priority: 100 }
}

function GuidelineEditor({ editor, setEditor, saving, onClose, onSubmit }) {
  if (!editor) return null
  return <AdminFormModal open title={editor.id ? 'AI 가이드라인 수정' : 'AI 가이드라인 작성'} description="ACTIVE 상태의 글은 선택한 AI 기능의 운영 문맥에 누적되어 전달됩니다." onClose={() => !saving && onClose()} onSubmit={onSubmit} submitLabel={saving ? '저장 중…' : '저장'} size="large">
    <FormGrid>
      <FormField label="제목" required wide hint="관리자가 목록에서 정책 목적을 바로 알아볼 수 있게 작성"><input required maxLength={200} value={editor.title} onChange={(event) => setEditor({ ...editor, title: event.target.value })} /></FormField>
      <FormField label="적용 범위" required><select value={editor.scope} onChange={(event) => setEditor({ ...editor, scope: event.target.value })}>{Object.entries(scopeLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></FormField>
      <FormField label="상태" required><select value={editor.status} onChange={(event) => setEditor({ ...editor, status: event.target.value })}><option value="DRAFT">작성중</option><option value="ACTIVE">적용중</option><option value="ARCHIVED">보관</option></select></FormField>
      <FormField label="우선순위" required hint="0~999 · 숫자가 높을수록 충돌 시 우선"><input type="number" min="0" max="999" required value={editor.priority} onChange={(event) => setEditor({ ...editor, priority: event.target.value })} /></FormField>
      <FormField label="AI 적용 지침" required wide hint="사용자에게 보여주는 문구가 아니라 AI가 실제 판단에 참고할 내부 운영 지침입니다."><textarea className="ai-guideline-body-input" required maxLength={10000} value={editor.body} onChange={(event) => setEditor({ ...editor, body: event.target.value })} placeholder={'예) 사용자가 예산을 명시하지 않았다면 저가 업체를 임의로 우선하지 않는다.\n포트폴리오 근거가 충분한 디렉터를 우선하고, 단순 인기 지표만으로 순위를 결정하지 않는다.'} /></FormField>
    </FormGrid>
  </AdminFormModal>
}

export default function AiGuidelines() {
  const [items, setItems] = useState([])
  const [context, setContext] = useState(null)
  const [contextScope, setContextScope] = useState('ALL')
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [scopeFilter, setScopeFilter] = useState('ALL')
  const [editor, setEditor] = useState(null)
  const [loading, setLoading] = useState(true)
  const [contextLoading, setContextLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [notice, setNotice] = useState('')

  const loadItems = async () => {
    setLoading(true)
    try {
      const response = await adminRequest('/api/v1/admin/ai/guidelines')
      setItems(Array.isArray(response?.data) ? response.data : [])
    } catch (error) { setNotice(error?.message || 'AI 가이드라인을 불러오지 못했습니다.') }
    finally { setLoading(false) }
  }

  const loadContext = async (scope = contextScope) => {
    setContextLoading(true)
    try {
      const response = await adminRequest(`/api/v1/admin/ai/guidelines/context?scope=${encodeURIComponent(scope)}`)
      setContext(response?.data || null)
    } catch (error) { setNotice(error?.message || 'AI 적용 문맥을 불러오지 못했습니다.'); setContext(null) }
    finally { setContextLoading(false) }
  }

  useEffect(() => { loadItems() }, [])
  useEffect(() => { loadContext(contextScope) }, [contextScope])

  const rows = useMemo(() => {
    const keyword = search.trim().toLowerCase()
    return items.filter((item) => {
      if (statusFilter !== 'ALL' && item.status !== statusFilter) return false
      if (scopeFilter !== 'ALL' && item.scope !== scopeFilter) return false
      if (keyword && ![item.title, item.body, scopeLabels[item.scope]].join(' ').toLowerCase().includes(keyword)) return false
      return true
    })
  }, [items, search, statusFilter, scopeFilter])

  const metrics = useMemo(() => ({
    active: items.filter((item) => item.status === 'ACTIVE').length,
    draft: items.filter((item) => item.status === 'DRAFT').length,
    archived: items.filter((item) => item.status === 'ARCHIVED').length,
  }), [items])

  const save = async (event) => {
    event.preventDefault()
    if (saving || !editor) return
    const title = editor.title.trim()
    const bodyText = editor.body.trim()
    const priority = Number(editor.priority)
    if (!title || !bodyText) return setNotice('제목과 AI 적용 지침을 입력해주세요.')
    if (!Number.isInteger(priority) || priority < 0 || priority > 999) return setNotice('우선순위는 0~999 사이 정수로 입력해주세요.')
    setSaving(true); setNotice('')
    const body = { title, body: bodyText, scope: editor.scope, status: editor.status, priority }
    try {
      if (editor.id) {
        await adminRequest(`/api/v1/admin/ai/guidelines/${encodeURIComponent(editor.id)}`, { method: 'PUT', headers: { 'If-Match': String(editor.version) }, body })
      } else {
        await adminRequest('/api/v1/admin/ai/guidelines', { method: 'POST', body })
      }
      setEditor(null)
      setNotice('AI 가이드라인을 DEV 운영 지식 원장에 저장했습니다.')
      await Promise.all([loadItems(), loadContext(contextScope)])
    } catch (error) { setNotice(error?.message || 'AI 가이드라인 저장에 실패했습니다.') }
    finally { setSaving(false) }
  }

  const archive = async (item) => {
    if (item.status === 'ARCHIVED') return
    if (!window.confirm(`'${item.title}' 가이드라인을 보관 처리할까요? 원장에서는 삭제되지 않습니다.`)) return
    try {
      await adminRequest(`/api/v1/admin/ai/guidelines/${encodeURIComponent(item.id)}`, { method: 'DELETE' })
      setNotice('가이드라인을 보관 처리했습니다. 과거 운영 이력은 유지됩니다.')
      await Promise.all([loadItems(), loadContext(contextScope)])
    } catch (error) { setNotice(error?.message || '가이드라인 보관 처리에 실패했습니다.') }
  }

  return <>
    <PageTitle eyebrow="AI POLICY KNOWLEDGE · LIVE DB" title="AI 추천 가이드라인" description="추천·견적 분석·검색에 사용할 운영 지식을 게시글처럼 등록하고 누적 관리합니다." actions={<button className="button primary" onClick={() => setEditor(emptyEditor())}>가이드라인 작성</button>} />
    <div className="policy-callout"><strong>운영 지식은 삭제하지 않고 누적합니다.</strong><span>ACTIVE만 AI 적용 문맥에 포함되며 DRAFT는 미적용, ARCHIVED는 과거 이력으로 보관됩니다. 충돌 시 우선순위가 높은 글이 먼저 적용됩니다.</span></div>
    {notice && <div className="admin-notice"><strong>안내</strong><span>{notice}</span></div>}

    <div className="metric-grid ai-guideline-metrics">
      <div className="metric-card metric-green"><span>적용중</span><strong>{metrics.active}개</strong><small>ACTIVE 운영 지식</small></div>
      <div className="metric-card metric-gold"><span>작성중</span><strong>{metrics.draft}개</strong><small>DRAFT · AI 미적용</small></div>
      <div className="metric-card metric-blue"><span>보관</span><strong>{metrics.archived}개</strong><small>ARCHIVED · 이력 유지</small></div>
      <div className="metric-card metric-orange"><span>현재 문맥</span><strong>{context ? `${Number(context.includedCount || 0)}/${Number(context.activeCount || 0)}` : '-'}</strong><small>{context?.version || '문맥 확인 중'}</small></div>
    </div>

    <div className="ai-guideline-layout">
      <Card title="가이드라인 원장" description="최신 수정본을 실제 DEV DB에서 조회합니다." className="table-card">
        <div className="table-toolbar ai-guideline-toolbar">
          <SearchField value={search} onChange={setSearch} placeholder="제목·내용 검색" />
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}><option value="ALL">전체 상태</option><option value="ACTIVE">적용중</option><option value="DRAFT">작성중</option><option value="ARCHIVED">보관</option></select>
          <select value={scopeFilter} onChange={(event) => setScopeFilter(event.target.value)}><option value="ALL">전체 범위</option>{Object.entries(scopeLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
        </div>
        <div className="table-scroll"><table className="data-table"><thead><tr><th>가이드라인</th><th>적용 범위</th><th>우선순위</th><th>상태</th><th>최종 수정</th><th>관리</th></tr></thead><tbody>{loading ? <tr><td colSpan="6">가이드라인을 불러오는 중입니다.</td></tr> : rows.length ? rows.map((item) => <tr key={item.id}><td className="ai-guideline-title-cell"><strong>{item.title}</strong><small>{item.body}</small></td><td>{scopeLabels[item.scope] || item.scope}</td><td><strong>{item.priority}</strong></td><td><StatusBadge status={item.status}>{statusLabels[item.status] || item.status}</StatusBadge></td><td>{formatDate(item.updatedAt, true)}<small>{item.updatedByEmail || item.createdByEmail || '-'}</small></td><td><div className="ai-guideline-actions"><button className="button soft" onClick={() => setEditor(emptyEditor(item))}>수정</button>{item.status !== 'ARCHIVED' && <button className="button danger-soft" onClick={() => archive(item)}>보관</button>}</div></td></tr>) : <tr><td colSpan="6"><EmptyState title="조건에 맞는 가이드라인이 없습니다." description="새 운영 지침을 작성하거나 필터를 변경해주세요." /></td></tr>}</tbody></table></div>
      </Card>

      <Card title="AI 적용 문맥 미리보기" description="현재 ACTIVE 글을 실제 AI에 전달할 순서와 범위를 확인합니다." className="ai-guideline-context-card">
        <label className="ai-guideline-context-filter"><span>확인 범위</span><select value={contextScope} onChange={(event) => setContextScope(event.target.value)}><option value="ALL">전체 ACTIVE 문맥</option><option value="GLOBAL">전체 공통만</option>{Object.entries(scopeLabels).filter(([value]) => value !== 'GLOBAL').map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
        {contextLoading ? <p>AI 적용 문맥을 불러오는 중입니다.</p> : context ? <>
          <div className="ai-guideline-context-stats"><div><span>활성 글</span><strong>{context.activeCount}개</strong></div><div><span>포함 글</span><strong>{context.includedCount}개</strong></div><div><span>문맥 길이</span><strong>{Number(context.includedChars || 0).toLocaleString()}자</strong></div></div>
          {context.truncated && <div className="admin-notice warning"><strong>문맥 제한</strong><span>비용과 프롬프트 크기를 보호하기 위해 낮은 우선순위의 일부 글이 현재 AI 문맥에서 제외됩니다.</span></div>}
          <div className="ai-guideline-context-version"><span>문맥 버전</span><code>{context.version}</code></div>
          <textarea className="ai-guideline-context-preview" readOnly value={context.text || '현재 적용중인 가이드라인이 없습니다.'} />
        </> : <EmptyState title="문맥을 확인할 수 없습니다." />}
      </Card>
    </div>

    <GuidelineEditor editor={editor} setEditor={setEditor} saving={saving} onClose={() => setEditor(null)} onSubmit={save} />
  </>
}
